import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import { isFirebaseConfigured } from '../firebase/app';
import { loadProgress, saveProgress, saveSession, saveLesson, savePracticeTest } from '../firebase/firestore';
import { createInitialProgress, applySessionResult, applyLessonComplete } from '../services/progressModel';
const ProgressContext = createContext(null);
const localKey = 'psat-demo-progress';
export function ProgressProvider({ children }) {
  const { user } = useAuth();
  const [progress, setProgress] = useState(() => JSON.parse(localStorage.getItem(localKey) || 'null') || createInitialProgress());
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    let active = true;
    async function hydrate() {
      if (!user || !isFirebaseConfigured) return;
      setLoading(true);
      try { const next = await loadProgress(user.uid); if (active) setProgress(next); } finally { if (active) setLoading(false); }
    }
    hydrate();
    return () => { active = false; };
  }, [user]);
  async function persist(next) {
    setProgress(next);
    localStorage.setItem(localKey, JSON.stringify(next));
    if (user && isFirebaseConfigured) await saveProgress(user.uid, next);
  }
  async function completeLesson(lesson, parsedLesson) {
    const next = applyLessonComplete(progress, lesson);
    await persist(next);
    if (user && isFirebaseConfigured) await saveLesson(user.uid, { lesson, parsedLesson });
  }
  async function recordSession(session) {
    const next = applySessionResult(progress, session);
    await persist(next);
    if (user && isFirebaseConfigured) await saveSession(user.uid, session);
  }
  async function recordTest(test) {
    const session = { ...test, mode: 'Full-Length Test', topics: Object.keys(progress.topicMastery), total: test.total, correct: test.correct, timeSpentSeconds: test.timeSpentSeconds };
    const next = applySessionResult({ ...progress, practiceTestsCompleted: (progress.practiceTestsCompleted || 0) + 1 }, session);
    next.testHistory = [{ estimatedScore: next.estimatedScore, accuracy: test.accuracy, date: new Date().toISOString() }, ...(progress.testHistory || [])].slice(0, 8);
    await persist(next);
    if (user && isFirebaseConfigured) await savePracticeTest(user.uid, { ...test, estimatedScore: next.estimatedScore });
  }
  const value = useMemo(() => ({ progress, loading, persist, completeLesson, recordSession, recordTest }), [progress, loading, user]);
  return <ProgressContext.Provider value={value}>{children}</ProgressContext.Provider>;
}
export function useProgress() { return useContext(ProgressContext); }
